import { useFormContext } from 'react-hook-form'
import { BefehlFormAuftrag, BefehlFormField } from '../Generic'
import { useTranslation } from 'react-i18next'
import { BetriebsstelleField, BetriebsstellenartField } from '../../form'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'
import { useMemo } from 'react'

export interface Auftrag23_10FormProps {
  befehlKennung: string
  fieldPrefix: string
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
}

export default function Auftrag23_10Form({
  fieldPrefix,
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag23_10FormProps>) {
  const formContext = useFormContext()
  const { control, formState, watch } = formContext
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const fieldPaths = useMemo(
    () => ({
      betriebsstellenart2311: `${fieldPrefix}.field_23_11.art`,
      betriebsstellenart2312: `${fieldPrefix}.field_23_12.art`
    }),
    [fieldPrefix]
  )

  const [betriebsstellenart2311, betriebsstellenart2312] = watch([
    fieldPaths.betriebsstellenart2311,
    fieldPaths.betriebsstellenart2312
  ])

  return (
    <BefehlFormAuftrag
      auftragKennung="23.10"
      testId={testId}
      fieldName="auftrag_23_10.enabled"
      readOnly={true}
      label={t('auftraege.23_10.label')}
    >
      <span data-testid={`${testId}-text`}>{t('auftraege.23_10.label')}</span>
      <BorderedBox
        data-testid={`${testId}-feld-23-11-bordered-box`}
        footer={
          <span
            className={'-mb-0.25'}
            data-testid={`${testId}-feld-23-11-label`}
          >
            {t('auftraege.23_10.23_11')}
          </span>
        }
      >
        <ZugmeldestelleWrapper
          betriebsstellenartField={
            <BefehlFormField
              name={fieldPaths.betriebsstellenart2311}
              control={control}
              label={t('auftraege.23_10.betriebsstellenart_label')}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-23-11-art`}
              render={({ field: field_23_11_art }) => (
                <BetriebsstellenartField
                  {...field_23_11_art}
                  label={t('auftraege.23_10.betriebsstellenart_label')}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-23-11-art-inner`}
                  selectClassName="w-full"
                />
              )}
            />
          }
          bestriebsstelleField={
            <BefehlFormField
              name={`${fieldPrefix}.field_23_11.name`}
              control={control}
              label={t('auftraege.23_10.zugmeldestelle_label')}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-23-11-name`}
              render={({ field: field_23_11_name }) => (
                <BetriebsstelleField
                  {...field_23_11_name}
                  label={t('auftraege.23_10.zugmeldestelle_label')}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-23-11-name-inner`}
                  typ={betriebsstellenart2311}
                />
              )}
            />
          }
        />
      </BorderedBox>
      <span className="pl-0.5" data-testid={`${testId}-text-bis`}>
        {t('auftraege.23_10.bis')}
      </span>
      <BorderedBox
        data-testid={`${testId}-feld-23-12-bordered-box`}
        footer={
          <span data-testid={`${testId}-feld-23-12-label`}>
            {' '}
            {t('auftraege.23_10.23_12')}
          </span>
        }
      >
        <ZugmeldestelleWrapper
          betriebsstellenartField={
            <BefehlFormField
              name={fieldPaths.betriebsstellenart2312}
              control={control}
              label={t('auftraege.23_10.betriebsstellenart_label')}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-23-12-art`}
              render={({ field: field_23_12_art }) => (
                <BetriebsstellenartField
                  {...field_23_12_art}
                  label={t('auftraege.23_10.betriebsstellenart_label')}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-23-12-art-inner`}
                  selectClassName="w-full"
                />
              )}
            />
          }
          bestriebsstelleField={
            <BefehlFormField
              name={`${fieldPrefix}.field_23_12.name`}
              control={control}
              label={t('auftraege.23_10.zugmeldestelle_label')}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-23-12-name`}
              render={({ field: field_23_12_name }) => (
                <BetriebsstelleField
                  {...field_23_12_name}
                  label={t('auftraege.23_10.zugmeldestelle_label')}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-23-12-name-inner`}
                  typ={betriebsstellenart2312}
                />
              )}
            />
          }
        />
      </BorderedBox>
    </BefehlFormAuftrag>
  )
}
